import { isEmpty } from 'lodash';
import type { Dictionary } from 'lodash';
import { FlowCXService } from '~/components/ClientExperience/OneFlow/services/flow-cx.service';
import { IFlowResponse, ITransformedFlowResponse } from '~/types/server.types';
import { getDifferencesBetweenObjs } from '~/utils/debug.utils';
import { trackError } from '~/services/logger/error.utils';
import { transformFlowResponse } from '~/utils/server-response.utils';
import { DEMO_LEAD_FORM_ID } from '~/constants/lead-forms.consts';

export class PublicFlowCxService extends FlowCXService {
    async _fetchFlowFromS3(flowId: string): Promise<IFlowResponse | null> {
        const companyId = this.clientPortalConfigStore?.clientPortalCompanyId;
        try {
            const response = await super.fetchPublicFlow(flowId, companyId);
            return response;
        } catch (error) {
            const errorMessage = '[PublicFlow]: failed to fetch flow from s3';
            const metadata = {
                flow_id: flowId,
                company_id: companyId,
                error: JSON.stringify(error)
            };
            trackError({
                error: error,
                message: errorMessage,
                context: metadata
            });
            // we're ok with empty response for now
            return null;
        }
    }

    _fetchFlow(flowId: string): Promise<IFlowResponse> {
        const company_id = this.clientPortalConfigStore?.clientPortalCompanyId;

        return super.fetchActivePublicFlow(flowId, company_id);
    }

    async fetchFlow(
        flowId: string,
        timezone: string
    ): Promise<ITransformedFlowResponse> {
        const isFlowIdExcludedFromCompare = [DEMO_LEAD_FORM_ID].includes(
            flowId
        );
        // 20% of cases should compare responses, always compare in local / staging
        const shouldCompareResponses = (() => {
            if (process.env.NODE_ENV !== 'production') {
                return true;
            }
            return isFlowIdExcludedFromCompare
                ? false
                : Math.floor(Math.random() * 100 + 1) <= 20;
        })();

        const flowFromS3Promise = this._fetchFlowFromS3(flowId);

        if (!shouldCompareResponses) {
            return transformFlowResponse(await flowFromS3Promise);
        }

        const [flowFromS3, flowFromApi] = await Promise.all([
            flowFromS3Promise,
            this._fetchFlow(flowId)
        ]);

        const flowToUse = this.decideOnFlowObjectToUse(flowFromApi, flowFromS3);
        return transformFlowResponse(flowToUse, timezone);
    }

    private decideOnFlowObjectToUse(
        flowFromApi: IFlowResponse,
        flowFromS3: IFlowResponse
    ) {
        if (!flowFromS3) {
            return flowFromApi;
        }

        const differences = getDifferencesBetweenObjs(flowFromApi, flowFromS3);
        this.filterFlowObjDifferences(differences); // mutates differences - removes things we dont care about

        if (!isEmpty(differences.removed) || !isEmpty(differences.added)) {
            const errorMessage =
                '[PublicFlow]: found differences between static and api flow responses';
            const metadata = {
                differences,
                flow_id: flowFromApi._id
            };
            trackError({
                error: new Error(errorMessage),
                context: metadata
            });

            // default back to api flow response if there were differences
            return flowFromApi;
        }

        // use new static flow from s3
        return flowFromS3;
    }

    private filterFlowObjDifferences(differences: {
        removed: Dictionary<any>;
        added: Dictionary<any>;
    }) {
        delete differences.removed?.revision_count;
        delete differences.added?.revision_count;
        return;
    }
}
